import {useEffect, type RefObject} from 'react'

type ContextStep = {root: RefObject<HTMLElement | null>; close: () => void; priority: number}
const steps = new Set<ContextStep>()
const visible = (element: HTMLElement) => !element.closest('[hidden], [inert], [data-context-active="false"]') && (!element.checkVisibility || element.checkVisibility())

/** Full-screen views over a screen (a picture or code, the image viewer): they own Back above any step of the screen behind them. */
export const OVERLAY_PRIORITY = 200

/**
 * The innermost visible step owns Back; mounted background contexts never do. `steps` leaves overlays alone (and takes nothing while
 * one is open, so a step behind it is never closed instead), `overlays` takes only them: the browser's Back closes an overlay with the
 * guard re-armed, as the note screen expects, while Android's back takes either in one call.
 */
export function dismissContextStep(range: 'all' | 'steps' | 'overlays' = 'all'): boolean {
  if ([...document.querySelectorAll<HTMLElement>('dialog[open], .account-panel')].some(visible)) return false
  const open = [...steps].reverse().sort((a, b) => b.priority - a.priority).filter(({root}) => root.current && visible(root.current))
  if (range === 'steps' && open.some(step => step.priority >= OVERLAY_PRIORITY)) return false
  const step = open.find(({priority}) => range === 'all' || (range === 'overlays' ? priority >= OVERLAY_PRIORITY : priority < OVERLAY_PRIORITY))
  if (!step) return false
  step.close()
  return true
}

export function useContextBack(enabled: boolean, root: RefObject<HTMLElement | null>, close: () => void, priority = 80) {
  useEffect(() => {
    if (!enabled) return
    const step = {root, close, priority}
    steps.add(step)
    return () => { steps.delete(step) }
  }, [enabled, root, close, priority])
}
